import { BanknoteIcon, PlusIcon, Trash2Icon, WalletIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { PAYMENT_METHODS, type PaymentMethod, type PaymentResponse } from "@/lib/endpoints"
import {
  PaymentStateBadge,
  usePaymentMethodLabel,
} from "@/features/payments/PaymentStateBadge"
import {
  isFeatureOff,
  useDeletePayment,
  useInvoicePayments,
  useRecordPayment,
} from "@/features/payments/use-payments"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DateField } from "@/components/DateField"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import { formatRupees, paiseToInputValue, parseRupeesToPaise } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * "What has been paid on this bill" — §17.5, on the invoice detail screen.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT SITS UNDER THE TOTALS AND ABOVE THE CANCEL CARD, and the position is the
 * argument in miniature. Outstanding follows the grand total because it is a
 * statement ABOUT that figure; the destructive card stays last because it always
 * does. It is NOT in the header beside "Make a copy": a receipt is not something
 * you do to the document.
 *
 * NOTHING HERE IS ON THE INVOICE. A tax invoice does not carry a running balance
 * — §9.2 lists what the page must show and this is not on it, which is also why
 * the API answers `payment` as a SIBLING of `data` rather than a field inside
 * it. The PDF is unchanged by anything on this card, in either direction.
 *
 * THREE GATES, ALL OF WHICH MUST HOLD BEFORE ANYTHING RENDERS:
 *
 *   1. `payment.view` (§2.1) — capability, never a role. Without it no request
 *      is even made: the server would refuse, and asking would only write a
 *      denial into the audit log.
 *   2. The `payments` module is ON. There is no `useFlag` on the client; the
 *      routes answer 404 when the flag is off (§18.3 — 404, not 403, so
 *      "forbidden" cannot leak unreleased work), and a 404 renders NOTHING. Not
 *      an error, not a retry button — trying again cannot switch a flag on.
 *   3. There is something to say: a bill that is owed, or receipts already
 *      recorded against one that is not.
 *
 * A CANCELLED BILL KEEPS ITS RECEIPTS AND OWES NOTHING (§17.5 rule E). Both
 * halves matter. The money came in — deleting the history to make a total tidy
 * is how books stop reconciling — and the bill leaves every outstanding figure
 * the moment it is cancelled. So the card still lists what was received, shows
 * no outstanding, and offers no way to record more.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function PaymentsCard({
  invoiceId,
  invoiceNumber,
  customerId,
  customerName,
}: {
  invoiceId: number
  invoiceNumber: string
  customerId: number
  /** From the document's FROZEN snapshot (§4) — who was billed that day. */
  customerName: string | null
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can } = useAuth()

  const query = useInvoicePayments(invoiceId, { enabled: can("payment.view") })

  const [recording, setRecording] = useState(false)
  const [deleting, setDeleting] = useState<PaymentResponse | null>(null)

  if (!can("payment.view")) return null

  /*
   * NO SKELETON. This card is absent on most installs (the module is behind a
   * flag) and on every draft bill, so a placeholder that usually resolves to
   * nothing would be a shape reserved for an absence — worse than the absence.
   * The totals card above it has already painted; this appears when it can.
   */
  if (query.isPending) return null

  if (query.isError) {
    // §18.3 — a 404 is "the module is off", not "no such bill". Say nothing.
    if (isFeatureOff(query.error)) return null
    return (
      <Alert variant="destructive">
        <AlertTitle>{t("payment.couldNotLoadTitle")}</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>
            {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
          </span>
          <Button
            variant="outline"
            onClick={() => {
              void query.refetch()
            }}
          >
            {t("btn.retry")}
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  /*
   * `summary` is CHECKED rather than assumed, though the wire contract
   * guarantees it. This card hangs off the invoice detail screen, and throwing
   * inside it unmounts the whole document — turning a missing secondary figure
   * into "the bill would not open", which is a far worse failure than the card
   * being absent for a moment.
   */
  const payments = query.data.data ?? []
  const { summary } = query.data
  if (summary === undefined) return null

  /*
   * A DRAFT OWES NOTHING AND HAS NO HISTORY, so it gets no card at all — §17.5
   * rule A: it is not a bill yet. A CANCELLED bill with receipts against it does
   * get one, because those receipts happened.
   */
  if (!summary.isOwed && payments.length === 0) return null

  const canRecord = can("payment.record") && summary.isOwed && summary.outstandingPaise > 0

  return (
    <Card data-testid="invoice-payments">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <WalletIcon aria-hidden="true" className="size-4 shrink-0" />
          {t("payment.cardTitle")}
        </CardTitle>
        {/* BESIDE the document status in the page header, never instead of it —
          * see PaymentStateBadge. Hidden for a bill that is not owed, because
          * "Paid" on a cancelled invoice would be a claim about a bill that
          * stands. */}
        {summary.isOwed ? <PaymentStateBadge state={summary.state} /> : null}
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {/*
          * EVERY FIGURE IS THE SERVER'S (§6). `receivedPaise` and
          * `outstandingPaise` arrive derived from the ledger — nothing on this
          * screen subtracts anything, and no balance is stored anywhere.
          */}
        <dl className="ml-auto flex w-full max-w-sm flex-col gap-2 text-sm">
          <div className="flex justify-between gap-6">
            <dt className="text-muted-foreground">{t("payment.billTotal")}</dt>
            <dd className="tabular-nums">{formatRupees(summary.grandTotalPaise)}</dd>
          </div>
          <div className="flex justify-between gap-6">
            <dt className="text-muted-foreground">{t("payment.received")}</dt>
            <dd className="tabular-nums">{formatRupees(summary.receivedPaise)}</dd>
          </div>
          {/*
            * §17.3 — CREDITED BACK, AND IT IS NOT A PAYMENT.
            *
            * ═══════════════════════════════════════════════════════════════════
            * THE ROW THE OUTSTANDING FIGURE WOULD OTHERWISE BE UNEXPLAINED BY.
            * Since credit notes exist the server computes
            * `outstanding = grandTotal − received − credited`, so on a credited
            * bill the three figures above would no longer subtract by eye — and
            * a column of numbers that does not add up is how the owner stops
            * believing the last one, which is the one he reads out to the
            * customer.
            *
            * IT IS NEVER LABELLED AS MONEY RECEIVED. Nothing arrived in the
            * bank; the bill got smaller (§34(1)). Folding it into "Received"
            * would make the statement disagree with the bank for a reason
            * nothing on screen explains — the same argument that keeps
            * `allReceiptsPaise` out of the statement's "Received" line.
            *
            * POSITIVE, AND SHOWN WITH NO MINUS SIGN, like every stored amount in
            * this system (§17.3). The LABEL carries the direction, which is
            * exactly why it has to be its own row rather than an adjustment
            * buried in the total.
            *
            * HIDDEN AT ZERO — which is every bill until one is credited. A
            * permanent "Credited back ₹0.00" on every invoice in the book is a
            * row the eye learns to skip, so the day it says ₹2,340 it would be
            * skipped too.
            * ═══════════════════════════════════════════════════════════════════
            */}
          {/* `> 0` rather than `!== 0`: the wire contract makes this required
            * and non-negative, and a `> 0` test also renders nothing if an old
            * build or a test double omits the key — where `!== 0` would hand
            * `undefined` to `formatPaise`, which throws and unmounts the whole
            * bill. That is the same argument as the `summary === undefined`
            * guard above. */}
          {summary.creditedPaise > 0 ? (
            <div className="flex justify-between gap-6">
              <dt className="text-muted-foreground">{t("payment.creditedBack")}</dt>
              <dd className="tabular-nums">{formatRupees(summary.creditedPaise)}</dd>
            </div>
          ) : null}
          <Separator />
          <div className="flex justify-between gap-6 text-base font-semibold">
            <dt>{t("payment.stillOwed")}</dt>
            <dd className="tabular-nums">{formatRupees(summary.outstandingPaise)}</dd>
          </div>
        </dl>

        {/*
          * §17.5 rule E, said in words rather than left to be inferred from a
          * zero. A cancelled bill with ₹50,000 against it shows the ₹50,000 and
          * an outstanding of nothing, which without this sentence reads as an
          * arithmetic error.
          */}
        {summary.isOwed ? null : (
          <p className="text-muted-foreground text-sm">{t("payment.cancelledNothingOwed")}</p>
        )}

        {payments.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("payment.noneYet")}</p>
        ) : (
          <ul className="flex flex-col divide-y rounded-lg border">
            {payments.map((row) => (
              <PaymentRow
                key={row.id}
                payment={row}
                onDelete={
                  can("payment.delete")
                    ? () => {
                        setDeleting(row)
                      }
                    : undefined
                }
              />
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {canRecord ? (
            <Button
              onClick={() => {
                setRecording(true)
              }}
            >
              <PlusIcon />
              {t("payment.recordAction")}
            </Button>
          ) : null}

          {/*
            * "Everything this customer owes" — the statement, which is the other
            * half of §17.5 and otherwise reachable only from the unpaid
            * register. One bill rarely answers the question being asked on the
            * phone; the customer's whole position usually does.
            */}
          <Button
            variant="outline"
            nativeButton={false}
            render={<Link to={routes.customerStatement(customerId)} />}
          >
            <BanknoteIcon />
            {t("payment.openStatement")}
          </Button>
        </div>
      </CardContent>

      {recording ? (
        <RecordPaymentDialog
          invoiceId={invoiceId}
          invoiceNumber={invoiceNumber}
          customerName={customerName}
          outstandingPaise={summary.outstandingPaise}
          onClose={() => {
            setRecording(false)
          }}
        />
      ) : null}

      <DeletePaymentDialog
        payment={deleting}
        onClose={() => {
          setDeleting(null)
        }}
      />
    </Card>
  )
}

/**
 * "Partly paid", BESIDE the status badge in the invoice's page header.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * REQUIREMENT: WHEREVER THE BILL'S STATUS IS SHOWN, SAY WHERE THE MONEY IS —
 * WITHOUT PRETENDING PAYMENT IS A DOCUMENT STATUS. So this renders NEXT TO
 * `DocumentStatusBadge`, never instead of it: the header reads "26-27/67
 * · Issued · Partly paid", which is two facts about one row rather than one
 * overloaded word. §17.5 refuses to put PAID into `InvoiceStatus` for exactly
 * this reason, and a header that swapped the badges would reintroduce the
 * confusion in the UI that the schema was designed to avoid.
 *
 * IT SHARES THE CARD'S QUERY. `useInvoicePayments` is keyed by invoice id, so
 * the badge and `PaymentsCard` below read ONE cache entry and issue ONE request
 * between them — and cannot disagree about the same bill on the same screen.
 *
 * ABSENT FOR ANYTHING NOT OWED. A DRAFT is not a bill yet and a CANCELLED bill
 * owes nothing (§17.5 rule A); "Paid" over a cancelled invoice would be a claim
 * about a document that does not stand. The receipts against it are still shown
 * in the card — the money happened — but no state word appears up here.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function InvoicePaymentBadge({
  invoiceId,
}: {
  invoiceId: number
}): React.JSX.Element | null {
  const { can } = useAuth()
  const query = useInvoicePayments(invoiceId, { enabled: can("payment.view") })

  if (!can("payment.view")) return null
  /*
   * Loading, a genuine failure, or the module switched off (§18.3 — a 404): in
   * every case the header simply carries no payment word. A badge that flickered
   * into place is worse than one that appears when it can.
   *
   * `summary` is checked rather than assumed. It is guaranteed by the wire
   * contract, and a header badge is still the wrong place to discover that a
   * response was malformed: throwing here unmounts the whole invoice screen,
   * turning a missing secondary figure into "the bill would not open".
   */
  const summary = query.data?.summary
  if (summary === undefined || !summary.isOwed) return null

  return <PaymentStateBadge state={summary.state} />
}

/** One receipt: when, how, what it was tied to, and how much. */
function PaymentRow({
  payment,
  onDelete,
}: {
  payment: PaymentResponse
  onDelete?: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const methodLabel = usePaymentMethodLabel()

  return (
    <li className="flex items-center gap-3 p-3">
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2 text-sm">
          {/*
            * THE DAY THE MONEY ARRIVED, not the day it was typed in. The API
            * keeps the two apart (`paidOn` vs `createdAt`) because entries are
            * routinely made days late from a passbook, and a list ordered by
            * entry date would not reconcile against a bank statement.
            */}
          <span className="font-medium tabular-nums">{formatDay(payment.paidOn)}</span>
          <span className="text-muted-foreground">{methodLabel(payment.method)}</span>
        </span>
        {payment.reference === null ? null : (
          /* The UTR / cheque no. / UPI ref / TDS certificate — the string that
           * settles a dispute six months later. */
          <span className="text-muted-foreground truncate text-xs tabular-nums">
            {payment.reference}
          </span>
        )}
        {payment.note === null ? null : (
          <span className="text-muted-foreground truncate text-xs">{payment.note}</span>
        )}
      </span>
      <span className="shrink-0 font-medium tabular-nums">
        {formatRupees(payment.amountPaise)}
      </span>
      {onDelete === undefined ? null : (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("payment.deleteAction", {
            amount: formatRupees(payment.amountPaise),
            date: formatDay(payment.paidOn),
          })}
          onClick={onDelete}
        >
          <Trash2Icon className="size-4" />
        </Button>
      )}
    </li>
  )
}

/**
 * Recording a receipt (§17.5).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE AMOUNT DEFAULTS TO WHAT IS STILL OWED, and that default is the feature.
 * Paid in full is the common case, and re-typing ₹1,45,600 into a box is an
 * invitation to a transposed digit that then has to be found by reconciling
 * against a bank statement. The figure comes off the server's `summary`; it is
 * pre-filled into an EDITABLE field, because part payment is normal in this
 * trade and the owner must be able to overwrite it in one gesture.
 *
 * THE CLIENT NEVER SENDS AN OUTSTANDING FIGURE BACK. The body is exactly what
 * was received — day, amount, method, reference, note — and the invoice comes
 * from the URL. The bound below is a courtesy so the owner is told before the
 * round trip, not an authorisation: the server locks the invoice row, re-sums
 * the ledger and refuses an overpayment inside one transaction (§17.5 rules B
 * and C), and it would refuse this even if the check here were deleted.
 *
 * WHY THE BOUND IS THERE AT ALL. An accepted overpayment makes outstanding
 * NEGATIVE, and a negative reduces the home screen's business-wide total — so
 * one fat-fingered entry quietly understates what the whole business is owed.
 * Plausible and invisible is the worst way to be wrong, which is why it is
 * refused in two places rather than one.
 *
 * MONEY IS PARSED, NEVER `parseFloat`-ED. `parseRupeesToPaise` splits the string
 * on the decimal point and reads two integers, so `67.50` cannot become
 * 6749.999… on the way to the wire. Nothing in this dialog divides.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function RecordPaymentDialog({
  invoiceId,
  invoiceNumber,
  customerName,
  outstandingPaise,
  onClose,
}: {
  invoiceId: number
  invoiceNumber: string
  customerName: string | null
  outstandingPaise: number
  onClose: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const methodLabel = usePaymentMethodLabel()
  const record = useRecordPayment(invoiceId)

  /*
   * TODAY IN ASIA/KOLKATA, not in the browser's zone. CLAUDE.md: dates and
   * financial-year logic are always IST. `todayInputValue()` reads the machine's
   * clock and would be right only by accident on a laptop set to another zone —
   * the son reconciling from abroad would date a receipt to yesterday.
   */
  const [paidOn, setPaidOn] = useState(
    () => istDayOfInstant(new Date().toISOString()) ?? "",
  )
  const [amount, setAmount] = useState(() => paiseToInputValue(outstandingPaise))
  const [method, setMethod] = useState<PaymentMethod>("NEFT")
  const [reference, setReference] = useState("")
  const [note, setNote] = useState("")

  /*
   * Follow the outstanding figure if it moves under an open dialog — his son
   * recording a receipt on the shop computer while this is open. Without this
   * the box would still hold the full amount and the save would be refused with
   * a 409 about a payment nobody on this screen can see.
   */
  useEffect(() => {
    setAmount(paiseToInputValue(outstandingPaise))
  }, [outstandingPaise])

  const amountPaise = parseRupeesToPaise(amount)
  const unreadable = amountPaise === null
  const notPositive = amountPaise !== null && amountPaise <= 0
  const tooMuch = amountPaise !== null && amountPaise > outstandingPaise
  const blocked = unreadable || notPositive || tooMuch || paidOn === ""

  const methodItems = PAYMENT_METHODS.map((value) => ({ value, label: methodLabel(value) }))

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("payment.recordTitle", { number: invoiceNumber })}</DialogTitle>
          <DialogDescription>
            {t("payment.recordHelp", {
              customer: customerName ?? t("lbl.customerUnknown"),
              amount: formatRupees(outstandingPaise),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payment-paid-on">{t("payment.paidOn")}</Label>
              <DateField id="payment-paid-on" value={paidOn} onChange={setPaidOn} />
              <p className="text-muted-foreground text-xs">{t("payment.paidOnHelp")}</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payment-amount">{t("payment.amount")}</Label>
              <Input
                id="payment-amount"
                /*
                 * `inputMode="decimal"` rather than `type="number"`: the numeric
                 * keypad on a phone without the spinner, the scroll-wheel
                 * accidents or the browser's own locale parsing. The string is
                 * read by `parseRupeesToPaise`, which accepts `1,45,600.50` and
                 * `₹6750` as typed.
                 */
                inputMode="decimal"
                className="tabular-nums"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value)
                }}
              />
              <p className="text-muted-foreground text-xs">
                {t("payment.amountHelp", { amount: formatRupees(outstandingPaise) })}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payment-method">{t("payment.methodLabel")}</Label>
            {/* `items` is what `SelectValue` resolves the trigger's words from —
              * without it the trigger prints the raw enum (`NEFT`, `CHEQUE`) at
              * the owner, in English, on a Marathi phone. */}
            <Select
              items={methodItems}
              value={method}
              onValueChange={(value) => {
                /*
                 * Base UI types the value as nullable and hands it straight
                 * over — there is no `event.target`. A null can only mean
                 * "cleared", which this control does not offer, so it is
                 * ignored rather than turned into a method nobody chose.
                 */
                if (typeof value === "string") setMethod(value as PaymentMethod)
              }}
            >
              <SelectTrigger id="payment-method" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="w-auto min-w-(--anchor-width)">
                {methodItems.map((item) => (
                  <SelectItem key={item.value} value={item.value} className="pl-3">
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payment-reference">{t("payment.reference")}</Label>
            <Input
              id="payment-reference"
              value={reference}
              onChange={(event) => {
                setReference(event.target.value)
              }}
              /* `reference @db.VarChar(191)` — capped at the column. */
              maxLength={191}
            />
            <p className="text-muted-foreground text-xs">{t("payment.referenceHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payment-note">{t("lbl.notes")}</Label>
            <Textarea
              id="payment-note"
              value={note}
              onChange={(event) => {
                setNote(event.target.value)
              }}
              maxLength={500}
              rows={2}
            />
          </div>

          {/*
            * SAID BEFORE THE BUTTON IS PRESSED, in the owner's words and naming
            * the figure. The server's own 409 says the same thing afterwards;
            * this is so the round trip is not how he finds out.
            */}
          {tooMuch ? (
            <Alert variant="destructive">
              <AlertDescription>
                {t("payment.tooMuch", { amount: formatRupees(outstandingPaise) })}
              </AlertDescription>
            </Alert>
          ) : notPositive || (unreadable && amount.trim() !== "") ? (
            <Alert variant="destructive">
              <AlertDescription>{t("payment.amountNotValid")}</AlertDescription>
            </Alert>
          ) : null}

          {record.isError ? (
            <Alert variant="destructive">
              <AlertDescription>
                {/*
                 * THE SERVER'S OWN SENTENCE. A 409 here names the outstanding
                 * amount, already Indian-grouped, and is information rather than
                 * a transient failure — there is deliberately no retry button.
                 */}
                {record.error instanceof ApiError
                  ? record.error.message
                  : t("err.somethingWentWrong")}
              </AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("btn.cancel")}
          </Button>
          <Button
            disabled={blocked || record.isPending}
            onClick={() => {
              if (amountPaise === null) return
              record.mutate(
                {
                  paidOn,
                  amountPaise,
                  /*
                   * `""` IS NOT SENT. The schema's optional text fields become
                   * NULL on the server for an empty string anyway, but omitting
                   * them keeps the request describing only what was actually
                   * entered.
                   */
                  ...(reference.trim() === "" ? {} : { reference: reference.trim() }),
                  ...(note.trim() === "" ? {} : { note: note.trim() }),
                  method,
                },
                {
                  onSuccess: (result) => {
                    toast.success(
                      t("payment.recorded", {
                        amount: formatRupees(result.data.amountPaise),
                      }),
                    )
                    onClose()
                  },
                },
              )
            }}
          >
            {record.isPending ? t("msg.saving") : t("payment.recordConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Removing a receipt that was never received (§17.5 rule D).
 *
 * IT IS AN ENTRY-ERROR FIX AND IT IS NOT A REFUND — the copy says so in as many
 * words, because "delete" beside a rupee amount reads like money going back. No
 * refund is modelled anywhere in this app and no negative payment may be
 * invented to stand for one: every outstanding figure is a `SUM` over this
 * ledger, and one negative row would show a customer as owed money he was never
 * billed.
 *
 * NO REASON IS ASKED FOR, unlike cancelling a bill. A cancelled invoice keeps a
 * number that is in a customer's hands and in a gap-free series the department
 * can ask about; a deleted payment retracts nothing anyone was ever shown. The
 * audit row is written in the same transaction as the delete and carries the
 * whole row, so the correction is recoverable from the log.
 */
function DeletePaymentDialog({
  payment,
  onClose,
}: {
  payment: PaymentResponse | null
  onClose: () => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const remove = useDeletePayment()

  if (payment === null) return null

  return (
    <AlertDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("payment.deleteTitle", {
              amount: formatRupees(payment.amountPaise),
              date: formatDay(payment.paidOn),
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t("payment.deleteHelp")}</AlertDialogDescription>
        </AlertDialogHeader>

        <p className="text-muted-foreground text-sm">{t("payment.deleteNotARefund")}</p>

        {remove.isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {remove.error instanceof ApiError
                ? remove.error.message
                : t("err.somethingWentWrong")}
            </AlertDescription>
          </Alert>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel render={<Button variant="outline" />}>
            {t("btn.cancel")}
          </AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => {
              remove.mutate(payment.id, {
                onSuccess: () => {
                  toast.success(t("payment.deleted"))
                  onClose()
                },
              })
            }}
          >
            {t("btn.delete")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
